import { ANY_OF_THOSE } from '../../constants/filter';
import { isLoadMoreAction } from './common';

// Lazy-load AlertContext emitters to avoid a circular import at module init:
// listActions → sharedCases → AlertContext → NotificationsInfo → notifications → listActionsHelpers
const getAlertEmitters = () => require('../../contexts/AlertContext');

export function onPending(state) {
  return Object.assign(state, { pending: true });
}

export function onPendingDone(state) {
  return Object.assign(state, { pending: false });
}

export function onPendingList(state, action) {
  return { ...state, pending: true, items: isLoadMoreAction(action) ? state.items : [] };
}

export function onItemDetails(state, action) {
  return Object.assign(state, { item: action.payload, pending: false });
}

export function onItemDetailsReject(state) {
  return Object.assign(state, { item: undefined, pending: false });
}

export function onSuccessAlert(state, action) {
  getAlertEmitters().successAlert('Success');
}

export function onSuccess(state, action) {
  // ? TODO: this throws an error here; why -> https://redux.js.org/Errors?code=3
  // onSuccessAlert(state, action);
  Object.assign(state, { pending: false });
}

export function onRejectAlert(state, action) {
  if (action.error?.code === 'SubscribedContentOnlyError') return;
  getAlertEmitters().errorAlert('api-error');
}

export function onReject(state, action) {
  // onRejectAlert(state, action);
  Object.assign(state, { pending: false });
}

export function extendBuilderWithAsyncAction(builder, action, cases = {}) {
  return builder
    .addCase(action.pending, typeof cases.onPending === 'function' ? cases.onPending : onPending)
    .addCase(action.rejected, typeof cases.onReject === 'function' ? cases.onReject : onReject)
    .addCase(action.fulfilled, typeof cases.onSuccess === 'function' ? cases.onSuccess : onSuccess);
}

export function extendBuilderWithFacetCounts(builder, action) {
  return builder
    .addCase(action.pending, (state, { meta }) => {
      state.facetCountsPending = true;
      state.facetCountsRequestId = meta.requestId;
    })
    .addCase(action.fulfilled, (state, { meta, payload }) => {
      if (meta.requestId !== state.facetCountsRequestId) return;
      state.facetCounts = payload || null;
      state.facetCountsPending = false;
    })
    .addCase(action.rejected, (state, { meta }) => {
      if (meta.requestId !== state.facetCountsRequestId) return;
      state.facetCountsPending = false;
    });
}

export const hasOwn = (object, key) => Object.hasOwn(object, key);

export const normalizeFieldFilter = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value?.data && value?.type) {
    return value;
  }

  if (value?.type && (value.from || value.to || (Array.isArray(value.value) && value.value.length))) {
    return value;
  }

  if (Array.isArray(value)) {
    return { data: value, type: ANY_OF_THOSE };
  }

  return { data: [value], type: ANY_OF_THOSE };
};

export const getNextOrder = (currentOrder, payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload?.orderBy) {
    return [payload.orderBy, payload.orderDir || payload.direction || 'asc'];
  }

  if (payload?.field) {
    return [payload.field, payload.direction || 'asc'];
  }

  const [currentOrderBy, currentOrderDir] = currentOrder || [];
  const nextOrderBy = payload;
  const nextOrderDir = currentOrderBy === nextOrderBy && currentOrderDir === 'asc' ? 'desc' : 'asc';

  return [nextOrderBy, nextOrderDir];
};

const mergeListFilters = (state, payload = {}, fieldKeys = []) => {
  const { fields = {}, text, reset, ...directFilters } = payload;
  const nextFields = { ...state.filters.fields };

  if (hasOwn(payload, 'text')) {
    state.filters.text = text || '';
  }

  fieldKeys.forEach((key) => {
    const hasDirectFilter = hasOwn(directFilters, key);
    const hasFieldFilter = hasOwn(fields, key);

    if (!hasDirectFilter && !hasFieldFilter) {
      return;
    }

    const normalizedValue = normalizeFieldFilter(hasDirectFilter ? directFilters[key] : fields[key]);

    if (normalizedValue) {
      nextFields[key] = normalizedValue;
    } else {
      delete nextFields[key];
    }
  });

  state.filters.fields = nextFields;

  if (reset && state.pagination) {
    state.pagination.page = 1;
  }
};

const applyOrder = (state, action) => {
  state.order = getNextOrder(state.order, action.payload);

  if (state.pagination) {
    state.pagination.page = 1;
  }
};

const applyPage = (state, action) => {
  if (state.pagination) {
    state.pagination.page = action.payload;
  }
};

const applyPerPage = (state, action) => {
  if (state.pagination) {
    state.pagination.perPage = action.payload;
    state.pagination.page = 1;
  }
};

export function extendBuilderWithListControls(builder, { setFilters, setOrder, setPage, setPerPage }, fieldKeys) {
  if (setFilters) {
    builder.addCase(setFilters, (state, action) => mergeListFilters(state, action.payload || {}, fieldKeys));
  }

  if (setOrder) {
    builder.addCase(setOrder, applyOrder);
  }

  if (setPage) {
    builder.addCase(setPage, applyPage);
  }

  if (setPerPage) {
    builder.addCase(setPerPage, applyPerPage);
  }

  return builder;
}
